import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, CreditCard, Loader2, ExternalLink, FileText } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Invoices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    // Check for payment success
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [location]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      fetchInvoices();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/invoices`, {
        withCredentials: true
      });
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out');
      return;
    }

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful!');
        // Remove session_id from URL
        window.history.replaceState({}, '', '/invoices');
        fetchInvoices();
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status');
    }
  };

  const handlePayInvoice = async (invoice) => {
    setProcessingPayment(invoice.invoice_id);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/checkout`,
        {
          invoice_id: invoice.invoice_id,
          origin_url: originUrl
        },
        { withCredentials: true }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
      setProcessingPayment(null);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      paid: 'bg-primary text-primary-foreground',
      pending: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50',
      overdue: 'bg-destructive/20 text-destructive border-destructive/50'
    };

    return (
      <Badge className={variants[status] || variants.pending} data-testid={`invoice-status-${status}`}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-mono">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="invoices-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border backdrop-blur-xl bg-background/80">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
            <div className="font-black text-xl uppercase tracking-tight">
              NEXT<span className="text-primary">/</span>GEN
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl sm:text-5xl font-black uppercase mb-2" data-testid="invoices-title">
            INVOICES
          </h1>
          <p className="text-muted-foreground mb-12">View and manage your invoices</p>

          {invoices.length === 0 ? (
            <Card className="p-12 text-center border-border">
              <p className="text-muted-foreground">No invoices found</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice, index) => (
                <motion.div
                  key={invoice.invoice_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="p-6 border-border hover:border-primary/50 transition-colors" data-testid={`invoice-card-${index}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold font-mono text-lg">
                            {invoice.wave_invoice_number ? `#${invoice.wave_invoice_number}` : invoice.invoice_id}
                          </h3>
                          {getStatusBadge(invoice.status)}
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">{invoice.description}</p>
                        <p className="text-xs text-muted-foreground">
                          Due: {format(new Date(invoice.due_date), 'MMM dd, yyyy')}
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <div className="text-right">
                          <p className="text-3xl font-black">
                            ${invoice.amount.toFixed(2)}
                          </p>
                          <p className="text-xs text-muted-foreground uppercase">{invoice.currency}</p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {invoice.wave_view_url && (
                            <Button
                              variant="outline"
                              onClick={() => window.open(invoice.wave_view_url, '_blank', 'noopener,noreferrer')}
                              className="font-bold uppercase"
                              data-testid={`view-invoice-btn-${index}`}
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              View Invoice
                            </Button>
                          )}

                          {invoice.wave_pdf_url && (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => window.open(invoice.wave_pdf_url, '_blank', 'noopener,noreferrer')}
                              title="Download PDF"
                              data-testid={`download-pdf-btn-${index}`}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          )}

                          {invoice.status === 'pending' && (
                            <Button
                              onClick={() => handlePayInvoice(invoice)}
                              disabled={processingPayment === invoice.invoice_id}
                              className="font-bold uppercase"
                              data-testid={`pay-invoice-btn-${index}`}
                            >
                              {processingPayment === invoice.invoice_id ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Processing
                                </>
                              ) : (
                                <>
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Pay Now
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Invoices;
